import type { UploadedMediaValue, UploadMediaObject } from '@/types/global'
import { STRAPI_URL } from '@/api/http'
import assetmissing from '@/assets/images/illustrations/assetmissing.svg'
import type { UploadedItem } from '@/types/formats.ts'

const DEFAULT_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024
const DEFAULT_IMAGE_UPLOAD_MAX_FILE_SIZE = 5 * 1024 * 1024
const DEFAULT_VIDEO_UPLOAD_MAX_FILE_SIZE = 50 * 1024 * 1024
const DEFAULT_DOCUMENT_UPLOAD_MAX_FILE_SIZE = 10 * 1024 * 1024
const DEFAULT_LOGO_UPLOAD_MAX_FILE_SIZE = 2 * 1024 * 1024

const mediaEnvironment = import.meta.env ?? {}

const DEFAULT_UPLOAD_IMAGE_ACCEPT_MIME_TYPES =
    'image/avif,image/png,image/webp,image/jpeg,image/jpg,image/svg+xml,image/heic,image/heif'
const DEFAULT_UPLOAD_VIDEO_ACCEPT_MIME_TYPES = 'video/mp4,video/quicktime'

const DEFAULT_DOCUMENT_ACCEPT_MIME_TYPES =
    'application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'

const MIME_TYPE_EXTENSIONS: Record<string, string[]> = {
    'image/avif': ['.avif'],
    'image/png': ['.png'],
    'image/webp': ['.webp'],
    'image/jpeg': ['.jpg', '.jpeg'],
    'image/jpg': ['.jpg', '.jpeg'],
    'image/svg+xml': ['.svg'],
    'image/heic': ['.heic'],
    'image/heif': ['.heif'],
    'video/mp4': ['.mp4'],
    'video/quicktime': ['.mov'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
}

const DEFAULT_SUPPORTED_IMAGE_EXTENSIONS = [
    'jpg',
    'jpeg',
    'png',
    'gif',
    'svg',
    'webp',
    'avif',
    'heic',
    'heif',
]
const DEFAULT_SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm']
const DEFAULT_SUPPORTED_DOCUMENT_EXTENSIONS = ['pdf', 'doc', 'docx']

export type FileCategory = 'image' | 'video' | 'videoandimage' | 'document' | 'unknown'
export type MediaValue = string | number | UploadedItem | UploadedMediaValue | null | undefined

export function getFileCategoryByExtension(
    fileName: string,
    allowedExtensions?: readonly string[]
): FileCategory {
    const extension = getExtensionFromFilename(fileName)

    if (!extension) return 'unknown'

    if (allowedExtensions && !allowedExtensions.map(normalizeFileExtension).includes(extension)) {
        return 'unknown'
    }

    if (getSupportedImageExtensions().includes(extension as string)) {
        return 'image'
    }

    if (getSupportedVideoExtensions().includes(extension as string)) {
        return 'video'
    }

    if (getSupportedDocumentExtensions().includes(extension as string)) {
        return 'document'
    }

    return 'unknown'
}

function convertExtensionListToArray(extensionList: string): readonly string[] {
    return extensionList
        .split(',')
        .map((ext: string) => ext.trim())
        .filter(Boolean)
        .map((ext: string) => (ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`))
}

function getExtensionFromFilename(fileName: string): string | null {
    const match = fileName.toLowerCase().match(/\.[^.]+$/)
    return match ? normalizeFileExtension(match[0]) : null
}

function normalizeFileExtension(ext: string): string {
    return ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
}

export function isUploadMediaObject(
    value: UploadedMediaValue | UploadedItem | undefined
): value is UploadMediaObject {
    return typeof value === 'object' && value !== null && 'url' in value
}

export const extractMediaUrl = (
    media: UploadedMediaValue | UploadedItem | string | undefined
): string | null => {
    // TODO: We should check why we sometimes get media as string here.
    if (!media || typeof media === 'string') return resolveMediaUrl(media as string)

    const mediaUrl = getMediaUrl(media)
    if (!mediaUrl) return null
    return resolveMediaUrl(mediaUrl)
}

export function getMediaUrl(media: UploadedMediaValue | UploadedItem | undefined): string | null {
    if (!media || typeof media === 'number') {
        return null
    }
    return media.url || null
}

export function resolveMediaUrl(mediaUrl: string | null) {
    if (!mediaUrl) return assetmissing
    return mediaUrl.startsWith('http') ? mediaUrl : STRAPI_URL + mediaUrl
}

export function getMediaAltText(media: UploadedMediaValue | undefined): string | null {
    if (!media || typeof media !== 'object') {
        return null
    }
    return media.alternativeText || null
}

export function getMediaName(media: UploadedMediaValue | UploadedItem | undefined): string | null {
    if (!media || typeof media !== 'object') {
        return null
    }
    return media.name || null
}

export function getDefaultUploadMaxFileSizeInBytes() {
    const maxUploadFileSizeValue =
        mediaEnvironment.VITE_IMAGE_UPLOAD_MAX_FILE_SIZE || DEFAULT_UPLOAD_MAX_FILE_SIZE
    if (typeof maxUploadFileSizeValue === 'string') {
        return parseInt(maxUploadFileSizeValue, 10)
    }
    return maxUploadFileSizeValue
}

export function getImageUploadMaxFileSizeInBytes() {
    const maxUploadFileSizeValue =
        mediaEnvironment.VITE_IMAGE_UPLOAD_MAX_FILE_SIZE || DEFAULT_IMAGE_UPLOAD_MAX_FILE_SIZE
    if (typeof maxUploadFileSizeValue === 'string') {
        return parseInt(maxUploadFileSizeValue, 10)
    }
    return maxUploadFileSizeValue
}

export function getVideoUploadMaxFileSizeInBytes() {
    const maxUploadFileSizeValue =
        mediaEnvironment.VITE_VIDEO_UPLOAD_MAX_FILE_SIZE || DEFAULT_VIDEO_UPLOAD_MAX_FILE_SIZE
    if (typeof maxUploadFileSizeValue === 'string') {
        return parseInt(maxUploadFileSizeValue, 10)
    }
    return maxUploadFileSizeValue
}

export function getDocumentUploadMaxFileSizeInBytes() {
    const maxUploadFileSizeValue =
        mediaEnvironment.VITE_DOCUMENT_UPLOAD_MAX_FILE_SIZE || DEFAULT_DOCUMENT_UPLOAD_MAX_FILE_SIZE
    if (typeof maxUploadFileSizeValue === 'string') {
        return parseInt(maxUploadFileSizeValue, 10)
    }
    return maxUploadFileSizeValue
}
export function formatFileSize(fileSizeInBytes: number): string {
    if (fileSizeInBytes < 1024) {
        return `${fileSizeInBytes} B`.replace('.', ',')
    }
    if (fileSizeInBytes < 1024 * 1024) {
        return `${(fileSizeInBytes / 1024).toFixed(1)} KB`.replace('.', ',')
    }

    return `${(fileSizeInBytes / (1024 * 1024)).toFixed(1)} MB`.replace('.', ',')
}

export function getLogoUploadMaxFileSizeInBytes() {
    const maxUploadFileSizeValue =
        mediaEnvironment.VITE_LOGO_UPLOAD_MAX_FILE_SIZE || DEFAULT_LOGO_UPLOAD_MAX_FILE_SIZE
    if (typeof maxUploadFileSizeValue === 'string') {
        return parseInt(maxUploadFileSizeValue, 10)
    }
    return maxUploadFileSizeValue
}

export function getUploadImageAcceptedMimeTypes() {
    return (
        mediaEnvironment.VITE_UPLOAD_IMAGE_ACCEPT_MIME_TYPES ||
        DEFAULT_UPLOAD_IMAGE_ACCEPT_MIME_TYPES
    )
}

export function getUploadVideoAcceptedMimeTypes() {
    return (
        mediaEnvironment.VITE_UPLOAD_VIDEO_ACCEPT_MIME_TYPES ||
        DEFAULT_UPLOAD_VIDEO_ACCEPT_MIME_TYPES
    )
}

export function getUploadVideoAndImageAcceptedMimeTypes() {
    return getUploadVideoAcceptedMimeTypes() + ',' + getUploadImageAcceptedMimeTypes()
}

export function getUploadDocumentAcceptedMimeTypes() {
    return (
        mediaEnvironment.VITE_UPLOAD_DOCUMENT_ACCEPT_MIME_TYPES ||
        DEFAULT_DOCUMENT_ACCEPT_MIME_TYPES
    )
}

export function getSupportedImageExtensions() {
    return convertExtensionListToArray(
        mediaEnvironment.VITE_SUPPORTED_IMAGE_EXTENSIONS ||
            DEFAULT_SUPPORTED_IMAGE_EXTENSIONS.join(',')
    )
}

export function getSupportedVideoExtensions() {
    return convertExtensionListToArray(
        mediaEnvironment.VITE_SUPPORTED_VIDEO_EXTENSIONS ||
            DEFAULT_SUPPORTED_VIDEO_EXTENSIONS.join(',')
    )
}

export function getSupportedDocumentExtensions() {
    return convertExtensionListToArray(
        mediaEnvironment.VITE_SUPPORTED_DOCUMENT_EXTENSIONS ||
            DEFAULT_SUPPORTED_DOCUMENT_EXTENSIONS.join(',')
    )
}

export function mediaUploadComponentGetMediaType(
    value: MediaValue,
    defaultMediaType: FileCategory = 'unknown'
) {
    if (!value) return defaultMediaType
    if (typeof value === 'string') {
        const fileCategory = getFileCategoryByExtension(value)
        return fileCategory !== 'unknown' ? fileCategory : defaultMediaType
    }

    if (typeof value !== 'object') return defaultMediaType

    if ('mime' in value && typeof value.mime === 'string') {
        const mime = value.mime
        if (mime.startsWith('image/')) return 'image'
        if (mime.startsWith('video/')) return 'video'
        if (mime.startsWith('application/pdf') || mime.includes('document')) return 'document'
    }

    if ('url' in value && typeof value.url === 'string') {
        const url = value.url.toLowerCase()
        const fileCategory = getFileCategoryByExtension(url)

        if (fileCategory !== 'unknown') {
            return fileCategory
        }
    }

    return defaultMediaType
}

export function mimeTypesToExtensions(mimeTypes: string[]): string[] {
    if (!Array.isArray(mimeTypes)) return []

    const extensions = mimeTypes.flatMap((type) => {
        return MIME_TYPE_EXTENSIONS[type] || []
    })

    return [...new Set(extensions)]
}

/**
 * Used for the user-facing "allowed file types" hint in upload components. MIME types without a
 * known extension mapping are omitted because raw MIME strings mean nothing to users.
 *
 * @example formatAcceptedFileTypes('image/png,image/jpeg') // '.png, .jpg, .jpeg'
 */
export function formatAcceptedFileTypes(accept: string): string {
    return mimeTypesToExtensions(accept.split(',').map((type) => type.trim())).join(', ')
}
